import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import DatePicker from "components/shared/inputs/DatePicker";
import Form, { Field } from "components/shared/Form";
import { useProfile } from "../../../../../hooks/useProfile";
import { useChangeUserInformationMutation } from "../../../../../api/useChangeUserInformationMutation";
import { useIsFieldDisabled } from "../../../../../hooks/useIsFieldDisabled";
import { useCompanyTimeZone } from "utils/hooks/company";
import { applyTimezone, dateTimeParseZone } from "utils/helpers/date";
import type { TFunction } from "i18next";

const createTerminateSchema = (t: TFunction, isDisabled: (key: string) => boolean) =>
    z.object({
        terminatedAt: z.date().optional().refine((value) => isDisabled("terminatedAt") || !!value, {
            message: t("required-termination-date"),
        }),
        reason: z.string().optional(),
    });

function Terminate({ close }: { close: () => void }) {
    const { t } = useTranslation();
    const { user } = useProfile();
    const timezone = useCompanyTimeZone();
    const getIsFieldDisabled = useIsFieldDisabled("peopleApiConnection.terminate");
    const { changeStatus } = useChangeUserInformationMutation();

    const defaultValues = useMemo(() => ({
        terminatedAt: user?.terminatedAt ? dateTimeParseZone(user.terminatedAt, "date") : undefined as Date | undefined,
        reason: "",
    }), [user]);

    const schema = useMemo(
        () => createTerminateSchema(t, getIsFieldDisabled),
        [t, getIsFieldDisabled],
    );

    const form = useForm({ defaultValues, resolver: zodResolver(schema) });

    const onFinish = (value: typeof defaultValues) => {
        const data = {
            terminatedAt: applyTimezone(value.terminatedAt, timezone, true),
            reason: value.reason,
            status: "terminated",
        };

        changeStatus.mutate(data, {
            onSuccess: () => {
                toast.success(t("changes-saved-successfully"));
                close();
            },
        });
    };

    return (
        <Form form={form} onFinish={onFinish} className="tw-flex tw-flex-col tw-h-full">
            <div className="tw-flex-1 tw-overflow-y-auto tw-min-h-0 tw-flex tw-flex-col tw-gap-4 tw-pb-4">
                <Field label={t("terminated-date")} name="terminatedAt">
                    <DatePicker
                        placeholder={t("terminated-date")}
                        disabled={getIsFieldDisabled("terminatedAt")}
                    />
                </Field>

                <Field label={t("reason")} name="reason">
                    <Input placeholder={t("reason")} />
                </Field>
            </div>

            <div className="tw-shrink-0 tw-flex tw-justify-end tw-gap-2 tw-pt-4 tw-border-t">
                <Button type="button" variant="outline" size="sm" onClick={close} disabled={changeStatus.isPending}>
                    {t("cancel")}
                </Button>
                <Button type="submit" size="sm" disabled={changeStatus.isPending}>
                    {t("save")}
                </Button>
            </div>
        </Form>
    );
}

export default Terminate;
